import { Box, chakra, Flex, Icon, IconButton } from '@chakra-ui/react'
import {
    chakraComponents,
    ChakraStylesConfig,
    ClearIndicatorProps,
    components,
    DropdownIndicatorProps,
    GroupProps,
    InputProps,
    LoadingIndicatorProps,
    OptionProps,
} from 'chakra-react-select'
import { Search, Trash2 } from 'emotion-icons/feather'
import { isSearchOption, SelectOption } from 'containers/jobs-board/components/options'

// Styles
export const getChakraStyles = ({
    // @ts-ignore
    menuProps,
}): ChakraStylesConfig<SelectOption, true> => ({
    container: provided => ({
        ...provided,
        cursor: 'text',
    }),
    control: provided => ({
        ...provided,
        minHeight: {
            base: '56px',
            md: '72px',
        },
        paddingLeft: {
            base: '2px',
            md: '16px',
        },
        paddingRight: {
            base: '2px',
            md: '16px',
        },
        alignItems: 'center',
    }),
    input: provided => ({
        ...provided,
        minW: '200px',
        w: '200px',
        height: {
            base: 'auto',
            md: '38px',
        },
    }),
    valueContainer: provided => ({
        ...provided,
        px: {
            base: '0',
            md: '8px',
        },
        py: {
            base: '0',
            md: '14px',
        },
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    indicatorsContainer: () => ({
        display: 'contents',
        'div:last-of-type': {
            order: -1, // moves Search icon to the left
        },
    }),
    placeholder: provided => ({
        ...provided,
        fontWeight: 'medium',
        display: { base: 'none', md: 'flex' },
    }),
    multiValue: provided => ({
        ...provided,
        borderRadius: '4px',
        bgColor: 'secondary_wh.300',
        color: 'white',
        display: { base: 'none', md: 'flex' },
    }),
    multiValueRemove: provided => ({
        ...provided,
        color: 'white',
        opacity: 1,
        strokeWidth: '1.5px',
        w: '16px',
        h: '16px',
    }),
    menuList: provided => ({
        ...provided,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxHeight: { base: 'fit-content', md: '40vh' },
        pl: { base: '24px', md: '36px' },
        pr: { base: '65px', md: '94px' },
        pt: { base: '16px', md: '24px' },
        pb: { base: '16px', md: '24px' },
        borderWidth: { base: '0', md: '1px' },
        borderRadius: { base: '0', md: '12px' },
        boxShadow: { base: 'xl', md: 'sm' },
    }),
    menu: provided => {
        const mobileMenuShift = '12px'

        return {
            ...provided,
            ...menuProps,
            left: { base: `-${mobileMenuShift}`, md: '0' },
            width: { base: `calc(100% + 2*${mobileMenuShift})`, md: '100%' },
            my: { base: '0', md: '8px' },
        }
    },
    groupHeading: provided => ({
        ...provided,
        display: 'flex',
        fontSize: '16px',
        color: 'text.secondary',
        width: '100%',
        p: '0px',
        mb: '12px !important',
    }),
})

// ====================== Components =====================

// Search icon
export const DropdownIndicator = (props: DropdownIndicatorProps<SelectOption, true>) => {
    return (
        <components.DropdownIndicator {...props}>
            <Icon
                order={1}
                fontSize="29px"
                color="black"
                strokeWidth="1.5px"
                as={Search}
            />
        </components.DropdownIndicator>
    )
}

// Trash icon to clear selections
export const ClearIndicator = (props: ClearIndicatorProps<SelectOption, true>) => {
    // FIXME: This button ignores keyboard events but has to fire clear on 'Enter'.
    return (
        <components.ClearIndicator {...props}>
            <IconButton
                aria-label="Clear search term and/or filters"
                display={{ base: 'none', md: 'flex' }}
                variant="unstyled"
                _focus={{
                    svg: {
                        stroke: 'secondary.500',
                    },
                    outline: 'none',
                }}
                _focusVisible={{
                    outline: 'none',
                }}
                _hover={{
                    svg: {
                        stroke: 'secondary.500',
                    },
                }}
                icon={
                    <Icon
                        order={1}
                        fontSize="28px"
                        stroke="text.secondary"
                        strokeWidth="1.5px"
                        as={Trash2}
                    />
                }
            />
        </components.ClearIndicator>
    )
}

// Group component to add flex and wrap to options
export const Group = (props: GroupProps<SelectOption, true>) => {
    return (
        <components.Group {...props}>
            <Box
                sx={{
                    ':not(div[role="button"])': {
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        flexDir: 'row',
                    },
                }}
            >
                {props.children}
            </Box>
        </components.Group>
    )
}

// Custom option component
export const Option = (props: OptionProps<SelectOption, true>) => {
    const isSearchTerm = isSearchOption(props.data)
    const isSuggestion = props.data?.type === 'Preset'

    const isFocused = props.isFocused // options are focusable by down/up arrows
    const focusedStyles = {
        backgroundColor: isSearchTerm ? 'gray.background' : 'gray.bg_dark',
    }
    const defaultStyles = isFocused
        ? focusedStyles
        : {
              backgroundColor: isSearchTerm ? 'white' : 'gray.background',
              borderColor: isSearchTerm ? 'gray.dividers' : 'gray.background',
          }

    return (
        <chakra.button
            w="fit-content"
            width="fit-content"
            fontSize="13px"
            fontWeight="medium"
            sx={{
                div: {
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    borderWidth: '1px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    ...defaultStyles,
                },
                'div:focus, div:hover': focusedStyles,
            }}
        >
            <components.Option {...props}>
                {isSearchTerm && !isSuggestion && (
                    <Icon
                        fontSize="17px"
                        color="black"
                        strokeWidth="1.5px"
                        as={Search}
                        mr="5px"
                    />
                )}
                {props.children}
            </components.Option>
        </chakra.button>
    )
}

export const Input = (props: InputProps<SelectOption, true>) => {
    return (
        <Flex w="100%">
            <chakraComponents.Input placeholder="Search jobs" {...props} />
        </Flex>
    )
}

// Custom spinner
export const LoadingIndicator = (props: LoadingIndicatorProps<SelectOption, true>) => {
    return (
        <chakraComponents.LoadingIndicator
            color="secondary_wh.300"
            thickness="2.68px"
            {...props}
        />
    )
}
